import React, { useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { updateClientRDV } from "../../services/rdvAgendaServices";
import { format, parseISO } from "date-fns";
import { fr, enGB } from "date-fns/locale";
import { LanguageContext } from "../../contexts/LanguageContext";
import { FaCheckCircle, FaTimes, FaSave, FaRegCalendarAlt, FaClock } from "react-icons/fa";

const toHHMM = (s) => String(s || "").slice(0, 5);

const ReservationRDVModal = ({ isOpen, onClose, rdvToBook, refreshList }) => {
  const { language } = useContext(LanguageContext);

  const i18n = useMemo(
    () => ({
      FR: {
        title: "Réserver un créneau",
        confirmed: "Rendez-vous confirmé",
        thanks: "Merci",
        date: "Date",
        time: "Heure",
        close: "Fermer",
        book: "Réserver",
        cancel: "Annuler",
        lastName: "Nom",
        firstName: "Prénom",
        phone: "Téléphone",
        note: "Note (optionnel)",
        success: "Rendez-vous réservé avec succès !",
        error: "Une erreur est survenue.",
        locale: fr,
      },
      EN: {
        title: "Book a slot",
        confirmed: "Appointment confirmed",
        thanks: "Thank you",
        date: "Date",
        time: "Time",
        close: "Close",
        book: "Book",
        cancel: "Cancel",
        lastName: "Last name",
        firstName: "First name",
        phone: "Phone",
        note: "Note (optional)",
        success: "Appointment booked successfully!",
        error: "Something went wrong.",
        locale: enGB,
      },
    }),
    []
  );

  const t = i18n[language] ?? i18n.FR;

  const [formData, setFormData] = useState({
    nom_client: "",
    prenom_client: "",
    tel_client: "",
    lieu: "Bureau",
    note_client: "",
  });

  const [isConfirmed, setIsConfirmed] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (isOpen && rdvToBook) {
      setIsConfirmed(false);
      setSending(false);
      setFormData({
        nom_client: "",
        prenom_client: "",
        tel_client: "",
        lieu: rdvToBook.lieu || "Bureau",
        note_client: "",
      });
    }
  }, [isOpen, rdvToBook]);

  // lock body scroll
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const formattedDate = rdvToBook?.date_rdv
    ? format(parseISO(rdvToBook.date_rdv), "dd/MM/yyyy", { locale: t.locale })
    : "";

  const formattedStart = toHHMM(rdvToBook?.heure_debut);
  const formattedEnd = toHHMM(rdvToBook?.heure_fin);

  const input =
    "w-full rounded-2xl px-4 py-3 text-sm outline-none transition " +
    "border border-black/10 dark:border-white/10 " +
    "bg-white/80 dark:bg-black/20 " +
    "text-gray-900 dark:text-white " +
    "placeholder:text-gray-500 dark:placeholder:text-gray-400 " +
    "focus:ring-2 focus:ring-primary focus:border-transparent";

  const buttonPrimary =
    "inline-flex items-center justify-center gap-2 rounded-2xl bg-primary px-6 py-3 text-white shadow " +
    "hover:opacity-95 transition active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed";

  const buttonGhost =
    "inline-flex items-center justify-center gap-2 rounded-2xl px-6 py-3 " +
    "border border-black/10 dark:border-white/10 bg-white/50 dark:bg-white/5 " +
    "text-gray-900 dark:text-white hover:bg-white/70 dark:hover:bg-white/10 transition";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!rdvToBook || sending) return;

    try {
      setSending(true);
      await updateClientRDV({
        id: rdvToBook.id,
        nom_client: formData.nom_client,
        prenom_client: formData.prenom_client,
        tel_client: formData.tel_client,
        lieu: formData.lieu,
        note_client: formData.note_client,
      });

      toast.success(t.success);
      setIsConfirmed(true);
    } catch (error) {
      console.error("Erreur réservation RDV :", error);
      toast.error(t.error);
    } finally {
      setSending(false);
    }
  };

  if (!isOpen || !rdvToBook) return null;

  return (
    <div
      className="fixed inset-0 z-[999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      aria-modal="true"
      role="dialog"
    >
      <div
        className="
          w-full max-w-xl overflow-hidden rounded-3xl
          border border-white/10
          bg-[#E8BB89]/80 dark:bg-neutral-850/90
          shadow-2xl backdrop-blur
        "
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div className="px-6 py-5 bg-gradient-to-r from-primary/95 to-primary-dark/95 text-white">
          <div className="flex items-center justify-between">
            <div className="font-satoshi font-extrabold text-lg">{t.title}</div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-2xl bg-white/10 hover:bg-white/15 transition flex items-center justify-center"
              aria-label="Close"
              type="button"
            >
              <FaTimes />
            </button>
          </div>

          {/* slot recap */}
          <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-white/90">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1">
              <FaRegCalendarAlt /> {formattedDate}
            </span>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1">
              <FaClock /> {formattedStart} – {formattedEnd}
            </span>
          </div>
        </div>

        {/* content */}
        <div className="p-6">
          {isConfirmed ? (
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center gap-2 text-primary font-extrabold text-xl">
                <FaCheckCircle />
                <span>{t.confirmed}</span>
              </div>

              <p className="text-gray-900 dark:text-white">
                {t.thanks}{" "}
                <strong>
                  {formData.prenom_client} {formData.nom_client}
                </strong>
                .
              </p>

              <p className="text-gray-800 dark:text-gray-300">
                <strong>{t.date} :</strong> {formattedDate}
                <br />
                <strong>{t.time} :</strong> {formattedStart} – {formattedEnd}
              </p>

              <button
                type="button"
                className={buttonPrimary}
                onClick={async () => {
                  setIsConfirmed(false);
                  onClose();
                  await refreshList?.();
                }}
              >
                {t.close}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                    {t.lastName}
                  </label>
                  <input
                    type="text"
                    name="nom_client"
                    value={formData.nom_client}
                    onChange={handleChange}
                    className={input}
                    placeholder={t.lastName}
                    required
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                    {t.firstName}
                  </label>
                  <input
                    type="text"
                    name="prenom_client"
                    value={formData.prenom_client}
                    onChange={handleChange}
                    className={input}
                    placeholder={t.firstName}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                  {t.phone}
                </label>
                <input
                  type="tel"
                  name="tel_client"
                  value={formData.tel_client}
                  onChange={handleChange}
                  className={input}
                  placeholder={t.phone}
                  required
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                  {t.note}
                </label>
                <textarea
                  name="note_client"
                  value={formData.note_client}
                  onChange={handleChange}
                  className={input}
                  rows={5}
                  placeholder={t.note}
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                <button type="submit" className={buttonPrimary} disabled={sending}>
                  <FaSave />
                  {sending ? "…" : t.book}
                </button>

                <button type="button" className={buttonGhost} onClick={onClose}>
                  <FaTimes />
                  {t.cancel}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReservationRDVModal;
